import z from "zod"
import { channelResponseSchema } from "../campaigns/commonTypes"
import { GeneratedRequirementSchema, RequirementSchema } from "../composer/requirements"
import { ConversationFlowEnum, ConversationTypeEnum } from "../conversations/response_data"
import {
  CapabilityCampaignAssignation,
  CapabilityExecutionStatus,
  CapabilityIdentifier,
  CapabilityKind,
} from "./dtos"
import { CapabilityEnablementResultSchema } from "./enablementReasons"
import { CapabilityReasonSchema } from "./reasons"

export const CapabilityResponseDataSchema = z.object({
  id: z.string(),
  identifier: z.nativeEnum(CapabilityIdentifier),
  name: z.string().nullable(),
  kind: z.enum(CapabilityKind),
  canBeUninstalled: CapabilityEnablementResultSchema,
  campaignAssignation: z.enum(CapabilityCampaignAssignation),
  campaignFlow: z.enum(ConversationFlowEnum).nullable().optional(),
  campaignIds: z.array(z.string()),
  timesUsed: z.number(),
  createdAt: z
    .union([z.date(), z.string()])
    .nullable()
    .transform((value) => (value instanceof Date ? value.toISOString() : value)),
  config: z.record(z.string(), z.any()).nullable(),
  requirements: z.array(RequirementSchema).nullable(),
  supportedProviders: z.array(z.string()),
  requiredCapabilities: z.array(z.enum(CapabilityIdentifier)),
  blockingCapabilities: z.array(z.enum(CapabilityIdentifier)),
  isNew: z.boolean(),
  isBuiltIn: z.boolean(),
  supportsConfigOverride: z.boolean(),
  isAvailableInWorkflows: z.boolean(),
  campaignConfigOverride: z.record(z.string(), z.unknown()).nullable().optional(),
})

export const CampaignCapabilityConfigResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  campaignId: z.bigint().transform(String),
  capabilityId: z.bigint().transform(String),
  configOverride: z.record(z.string(), z.unknown()),
  createdAt: z.date().transform((d) => d.toISOString()),
  updatedAt: z.date().transform((d) => d.toISOString()),
})

export const CapabilitiesResponseDataSchema = z.array(CapabilityResponseDataSchema)

export const MarketplaceCapabilityResponseDataSchema = z.object({
  identifier: z.enum(CapabilityIdentifier),
  kind: z.enum(CapabilityKind),
  canBeInstalled: CapabilityEnablementResultSchema,
  supportedProviders: z.array(z.string()),
  requiredCapabilities: z.array(z.enum(CapabilityIdentifier)),
  blockingCapabilities: z.array(z.enum(CapabilityIdentifier)),
  isNew: z.boolean(),
})

export const CapabilitiesMarketplaceResponseDataSchema = z.array(
  MarketplaceCapabilityResponseDataSchema,
)

export const CapabilityExecutionResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  conversationId: z.bigint().transform(String),
  capabilityId: z.bigint().transform(String),
  result: z.any().nullable(),
  reason: CapabilityReasonSchema.nullable(),
  evaluation: z.any().nullable(),
  provider: z.string().nullable().optional(),
  status: z.nativeEnum(CapabilityExecutionStatus),
  cacheExecutionId: z.bigint().transform(String).nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
  conversation: z
    .object({
      customerId: z.bigint().transform(String),
      type: z.enum(ConversationTypeEnum),
      channel: channelResponseSchema,
      customer: z.object({
        name: z.string().nullable(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
      }),
      campaign: z
        .object({
          id: z.bigint().transform(String),
          name: z.string().nullable(),
        })
        .nullable(),
    })
    .nullable(),
})

export const CapabilityExecutionsResponseDataSchema = z.array(CapabilityExecutionResponseDataSchema)

export const CapabilityExecutionsAnalyticsResponseDataSchema = z.object({
  successRate: z.number(),
  successfulConversations: z.number().int(),
  totalConversations: z.number().int(),
  series: z.array(
    z.object({
      date: z.string(),
      Success: z.number().int(),
      Unsuccess: z.number().int(),
      Cached: z.number().int(),
      Error: z.number().int(),
      Skipped: z.number().int(),
    }),
  ),
})

export const TestWebhookResponseDataSchema = z.object({
  valid: z.boolean(),
  error: z.string().optional(),
  responseBody: z.string().nullish(),
})

export const ExtractCapabilityRequirementsResponseDataSchema = z.object({
  requirements: z.array(GeneratedRequirementSchema).min(1),
})
